import { useState } from 'react'
import { useQuery } from '@apollo/react-hooks'
import cookie from 'isomorphic-cookie'
import { find } from 'lodash'
import { useFeature, useFlag } from 'toggled'

import { userPopupQuery } from '~/gql/queries'
import useIsomorphicLayoutEffect from '~/hooks/use-isomorphic-layout-effect'
import useModalManager from '~/hooks/use-modal-manager'
import useVendor from '~/hooks/use-vendor'
import { cookieNames, flags, orderStatuses } from '~/utils/constants'

import BuyingInStoreSelector from '../buying-in-store-selector'
import CookiesAlert from '../cookies-alert'
import LegalAgeModal from '../legal-age-modal'
import NeedsToAcceptTermsAndConditions from '../needs-to-accept-terms-and-conditions'
import NeedsToBeUpdated from '../needs-to-be-updated'
import OrderToReviewModal from '../order-to-review-modal'
import OrdersToBeApprovedAlert from '../orders-to-be-approved-alert'
import UnderConstructionModal from '../under-construction-modal'

const PopUp = () => {
  const orderReviewFF = useFeature(flags.ORDER_REVIEW)

  const variables = {
    orderStatusSlug: orderStatuses.TO_BE_APPROVED,
    skipOrderReviewReminder: !orderReviewFF,
  }

  const { data } = useQuery(userPopupQuery, { variables })

  const user = data?.user ?? {}

  const storeId = cookie.load(cookieNames.STORE_ID)

  const acceptedCookies = cookie.load(cookieNames.ACCEPTED_COOKIES)

  const legalAgeModalClosed = cookie.load(cookieNames.LEGAL_AGE_MODAL_CLOSED)

  const vendor = useVendor()

  const orders = user?.orders?.items

  const orderToApprove = find(orders, {
    status: { slug: orderStatuses.TO_BE_APPROVED },
  })

  const needsUserApprovalForCookies = useFlag(flags.COOKIES_NEED_USER_APPROVAL)

  const hasStoreNavigation = useFlag(flags.STORE_NAVIGATION)

  const hasLegalAgeModal = useFlag(flags.LEGAL_AGE_MODAL)

  const hasLoginRequiredFF = useFlag(flags.LOGIN_REQUIRED)

  const hasUnderConstructionModal = useFlag(flags.UNDER_CONSTRUCTION_MODAL)

  const { openModal, closeModal } = useModalManager()

  const isLoggedOutPrivateLogin = hasLoginRequiredFF && !user.isLoggedIn

  const externalPaths = ['/terminos-y-condiciones', '/politica-de-privacidad', '/libro-de-reclamaciones']

  const pathIsExternalToNavigation = externalPaths.includes(window.location.pathname)

  useIsomorphicLayoutEffect(() => {
    if (pathIsExternalToNavigation) {
      return
    }

    let modalId

    if (hasUnderConstructionModal) {
      modalId = openModal(UnderConstructionModal)
    } else if (user.needsToBeUpdated) {
      modalId = openModal(NeedsToBeUpdated, { user })
    } else if (vendor.legalTermsUrl && user.needsToAcceptTermsAndConditions) {
      modalId = openModal(NeedsToAcceptTermsAndConditions, { user })
    } else if (hasStoreNavigation && !storeId) {
      modalId = openModal(BuyingInStoreSelector)
    } else if (orderToApprove) {
      modalId = openModal(OrdersToBeApprovedAlert, {
        initialOrderStatusId: orderToApprove.status.id,
      })
    }

    return () => {
      if (modalId) {
        closeModal(modalId)
      }
    }
  }, [user.needsToBeUpdated, user.needsToAcceptTermsAndConditions, storeId, user.isLoggedIn])

  const isHomePath = window.location.pathname === '/'

  const showOrderReviewReminder = user?.needsReminderForOrderReview?.status

  const orderIdToReview = user?.needsReminderForOrderReview?.orderId

  const [orderReviewReminderClosed, setOrderReviewReminderClosed] = useState(false)

  useIsomorphicLayoutEffect(() => {
    if (pathIsExternalToNavigation || isLoggedOutPrivateLogin) {
      return
    }

    let modalId

    if (hasLegalAgeModal && !legalAgeModalClosed) {
      modalId = openModal(LegalAgeModal)
    } else if (needsUserApprovalForCookies && !acceptedCookies) {
      modalId = openModal(CookiesAlert)
    } else if (showOrderReviewReminder && isHomePath && !orderReviewReminderClosed) {
      modalId = openModal(OrderToReviewModal, {
        orderId: orderIdToReview,
        orderStatus: orderReviewFF.settings?.status,
        onClose: () => setOrderReviewReminderClosed(true),
      })
    }

    return () => {
      if (modalId) {
        closeModal(modalId)
      }
    }
  }, [window.location.pathname, showOrderReviewReminder])

  return null
}

export default PopUp
